import { Spin } from "./Spin";
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type MouseEventHandler, type ReactNode, type Ref } from "react";

import { cx, useKitDisabled, useKitSize } from "./fieldContext";
import { useDialogAction } from "./dialogActionScope";

const COLOR_DANGER = "danger";
const COLOR_RED = "red";

export type ButtonType = "primary" | "default" | "dashed" | "link" | "text";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLElement>, "type" | "onClick"> {
  /** Erscheinung wie bei Antd: `primary` (Hauptaktion), `default`, `dashed`, `text`, `link`. */
  type?: ButtonType;
  /** HTML-Typ des Knopfs (Default: "button"). */
  htmlType?: "button" | "submit" | "reset";
  danger?: boolean;
  /** Durchscheinend auf dunklen Flächen. */
  ghost?: boolean;
  icon?: ReactNode;
  iconPlacement?: "start" | "end";
  /** @deprecated wie bei Antd: `iconPlacement`. */
  iconPosition?: "start" | "end";
  loading?: boolean | { delay?: number };
  size?: "small" | "middle" | "large";
  shape?: "default" | "circle" | "round";
  block?: boolean;
  /** Mit `href` entsteht ein Link in Knopfoptik. */
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  rel?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  /** Antd-6-Schreibweise statt `type`/`danger`: `variant` (solid, outlined, dashed, filled, text, link) und `color`. */
  variant?: "solid" | "outlined" | "dashed" | "filled" | "text" | "link";
  color?: "default" | "primary" | "danger" | (string & {});
}

const VARIANT_TYPES: Record<NonNullable<ButtonProps["variant"]>, ButtonType> = { solid: "primary", outlined: "default", dashed: "dashed", filled: "text", text: "text", link: "link" };

/**
 * Knopf des Kits (natives `<button>`, mit `href` ein `<a>`). Schnittstelle wie Antd `Button`, damit
 * Tooltip, Dropdown, Popconfirm und Upload ihn weiter auslösen (Ref und Ereignisse werden durchgereicht).
 * Ein gesperrtes Formular (`<Form disabled>`) sperrt ihn wie zuvor.
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  { type: typeProp, variant, color, htmlType = "button", danger: dangerProp, ghost, icon, iconPlacement, iconPosition, loading, size, shape, block, href, target, rel, className, children, disabled, onClick, ...rest },
  ref,
) {
  const action = useDialogAction();
  const invoke: typeof onClick = event => { if (action) action.invoke(() => onClick?.(event)); else onClick?.(event); };
  const type: ButtonType = typeProp ?? (variant ? VARIANT_TYPES[variant] : "default");
  const danger = dangerProp ?? (color === COLOR_DANGER || color === COLOR_RED);
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const busy = Boolean(loading);
  const hasLabel = children != null && children !== false && children !== "";
  const placement = iconPlacement ?? iconPosition ?? "start";
  const glyph = busy ? <Spin inline /> : icon;
  const classNames = cx(
    "ck-btn",
    `ck-btn--${type}`,
    danger && "ck-btn--danger",
    ghost && "ck-btn--ghost",
    variant === "filled" && "ck-btn--filled",
    mergedSize === "small" && "ck-btn--sm",
    mergedSize === "large" && "ck-btn--lg",
    shape === "circle" && "ck-btn--circle",
    shape === "round" && "ck-btn--round",
    block && "ck-btn--block",
    !hasLabel && glyph != null && "ck-btn--icon-only",
    busy && "is-loading",
    className,
  );
  // Produktneutrales Merkmal der Hauptaktion: Enter im Dialog löst sie aus (shared `dialogKeyboard`).
  const primaryAction = type === "primary" && !danger ? true : undefined;
  const content = (
    <>
      {glyph != null && placement === "start" ? <span className="ck-btn-icon" aria-hidden="true">{glyph}</span> : null}
      {hasLabel ? <span className="ck-btn-label">{children}</span> : null}
      {glyph != null && placement === "end" ? <span className="ck-btn-icon" aria-hidden="true">{glyph}</span> : null}
    </>
  );

  if (href) {
    return (
      <a
        ref={ref as Ref<HTMLAnchorElement>}
        href={mergedDisabled ? undefined : href}
        target={target}
        rel={rel}
        className={classNames}
        aria-disabled={mergedDisabled || undefined}
        aria-busy={busy || undefined}
        data-primary-action={primaryAction}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
        onClick={invoke}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type={htmlType}
      className={classNames}
      disabled={mergedDisabled || busy}
      aria-busy={busy || undefined}
      data-primary-action={primaryAction}
      {...rest}
      onClick={invoke}
    >
      {content}
    </button>
  );
});
